import React, { useState, useEffect } from 'react';
import { db } from '../../../utils/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Save, Loader, Plus, Trash2 } from 'lucide-react';
import { FooterContent, FooterLink } from '../../../types';
import { DEFAULT_FOOTER_CONTENT } from '../../../constants';

const FooterManager: React.FC = () => {
  const [footer, setFooter] = useState<FooterContent>(DEFAULT_FOOTER_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchFooter();
  }, []);

  const fetchFooter = async () => {
    try {
      const docRef = doc(db, 'content', 'footer');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setFooter({ ...DEFAULT_FOOTER_CONTENT, ...(docSnap.data() as Partial<FooterContent>) });
      }
    } catch (error) {
      console.error('Error fetching footer content:', error);
      setMessage({ type: 'error', text: 'Failed to load footer content' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await setDoc(
        doc(db, 'content', 'footer'),
        {
          ...footer,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      setMessage({ type: 'success', text: 'Footer saved successfully' });
    } catch (error) {
      console.error('Error saving footer content:', error);
      setMessage({ type: 'error', text: 'Failed to save footer content' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof FooterContent, value: string) => {
    setFooter((prev) => ({ ...prev, [field]: value }));
  };

  const updateColumnTitle = (colIndex: number, title: string) => {
    setFooter((prev) => ({
      ...prev,
      columns: prev.columns.map((col, i) => (i === colIndex ? { ...col, title } : col)),
    }));
  };

  const updateColumnLink = (colIndex: number, linkIndex: number, field: keyof FooterLink, value: string) => {
    setFooter((prev) => ({
      ...prev,
      columns: prev.columns.map((col, i) =>
        i === colIndex
          ? { ...col, links: col.links.map((link, j) => (j === linkIndex ? { ...link, [field]: value } : link)) }
          : col
      ),
    }));
  };

  const addColumnLink = (colIndex: number) => {
    setFooter((prev) => ({
      ...prev,
      columns: prev.columns.map((col, i) =>
        i === colIndex ? { ...col, links: [...col.links, { label: '', url: '' }] } : col
      ),
    }));
  };

  const removeColumnLink = (colIndex: number, linkIndex: number) => {
    setFooter((prev) => ({
      ...prev,
      columns: prev.columns.map((col, i) =>
        i === colIndex ? { ...col, links: col.links.filter((_, j) => j !== linkIndex) } : col
      ),
    }));
  };

  const addColumn = () => {
    setFooter((prev) => ({
      ...prev,
      columns: [...prev.columns, { title: '', links: [{ label: '', url: '' }] }],
    }));
  };

  const removeColumn = (colIndex: number) => {
    setFooter((prev) => ({
      ...prev,
      columns: prev.columns.filter((_, i) => i !== colIndex),
    }));
  };

  const updateBottomLink = (linkIndex: number, field: keyof FooterLink, value: string) => {
    setFooter((prev) => ({
      ...prev,
      bottomLinks: prev.bottomLinks.map((link, j) => (j === linkIndex ? { ...link, [field]: value } : link)),
    }));
  };

  const addBottomLink = () => {
    setFooter((prev) => ({
      ...prev,
      bottomLinks: [...prev.bottomLinks, { label: '', url: '' }],
    }));
  };

  const removeBottomLink = (linkIndex: number) => {
    setFooter((prev) => ({
      ...prev,
      bottomLinks: prev.bottomLinks.filter((_, j) => j !== linkIndex),
    }));
  };

  if (loading)
    return (
      <div className="py-8">
        <Loader className="animate-spin" />
      </div>
    );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold text-xl">Footer</h2>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-black text-white px-6 py-2 font-heading font-bold uppercase tracking-widest hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2 text-sm"
        >
          {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
          Save Changes
        </button>
      </div>

      {message && (
        <div
          className={`p-3 mb-6 text-sm ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <p className="text-sm text-gray-500 mb-8">
        Configure the footer that appears at the bottom of every page: the newsletter block, the link columns, the
        Instagram link, and the bottom bar. Use site-relative links like <span className="font-mono">/shop</span> for
        internal pages; links starting with <span className="font-mono">http</span> open in a new tab.
      </p>

      <div className="space-y-10">
        {/* Newsletter */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Newsletter</h3>
          <div className="space-y-4 max-w-2xl">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Heading</label>
              <input
                type="text"
                value={footer.newsletterHeading}
                onChange={(e) => updateField('newsletterHeading', e.target.value)}
                className="w-full text-sm p-2 border border-gray-200"
                placeholder="The archive opens 14 August."
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Text</label>
              <textarea
                value={footer.newsletterText}
                onChange={(e) => updateField('newsletterText', e.target.value)}
                className="w-full text-sm p-2 border border-gray-200"
                rows={2}
                placeholder="Leave an address. You'll know when something surfaces."
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                Success Message
              </label>
              <textarea
                value={footer.newsletterSuccessMessage}
                onChange={(e) => updateField('newsletterSuccessMessage', e.target.value)}
                className="w-full text-sm p-2 border border-gray-200"
                rows={2}
                placeholder="You're on the list."
              />
              <p className="text-xs text-gray-400 mt-1">Shown after a visitor successfully subscribes</p>
            </div>
          </div>
        </section>

        {/* Link Columns */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500">Link Columns</h3>
            <button
              onClick={addColumn}
              className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-black"
            >
              <Plus size={14} /> Add Column
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {footer.columns.map((column, colIndex) => (
              <div key={colIndex} className="border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="text"
                    value={column.title}
                    onChange={(e) => updateColumnTitle(colIndex, e.target.value)}
                    className="flex-1 text-sm p-2 border border-gray-200 font-heading font-bold uppercase tracking-widest"
                    placeholder="Column Title"
                  />
                  <button
                    onClick={() => removeColumn(colIndex)}
                    className="text-gray-400 hover:text-red-600"
                    title="Remove column"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="space-y-2">
                  {column.links.map((link, linkIndex) => (
                    <div key={linkIndex} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={link.label}
                        onChange={(e) => updateColumnLink(colIndex, linkIndex, 'label', e.target.value)}
                        className="flex-1 min-w-0 text-sm p-2 border border-gray-200"
                        placeholder="Label"
                      />
                      <input
                        type="text"
                        value={link.url}
                        onChange={(e) => updateColumnLink(colIndex, linkIndex, 'url', e.target.value)}
                        className="flex-1 min-w-0 text-sm p-2 border border-gray-200 font-mono"
                        placeholder="/shop"
                      />
                      <button
                        onClick={() => removeColumnLink(colIndex, linkIndex)}
                        className="text-gray-400 hover:text-red-600"
                        title="Remove link"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addColumnLink(colIndex)}
                    className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-black mt-2"
                  >
                    <Plus size={12} /> Add Link
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Social & Bottom Bar */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Social & Bottom Bar</h3>
          <div className="space-y-4 max-w-2xl">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                Instagram URL
              </label>
              <input
                type="text"
                value={footer.instagramUrl}
                onChange={(e) => updateField('instagramUrl', e.target.value)}
                className="w-full text-sm p-2 border border-gray-200 font-mono"
                placeholder="https://instagram.com/4hvnsk8"
              />
              <p className="text-xs text-gray-400 mt-1">Leave empty to hide the Instagram icon</p>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                Copyright Name
              </label>
              <input
                type="text"
                value={footer.copyrightName}
                onChange={(e) => updateField('copyrightName', e.target.value)}
                className="w-full text-sm p-2 border border-gray-200"
                placeholder="4HVNSK8"
              />
              <p className="text-xs text-gray-400 mt-1">
                Shown as "© {new Date().getFullYear()} {footer.copyrightName || 'NAME'}. ALL RIGHTS RESERVED." — the
                year updates automatically
              </p>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                Bottom Links
              </label>
              <div className="space-y-2">
                {footer.bottomLinks.map((link, linkIndex) => (
                  <div key={linkIndex} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) => updateBottomLink(linkIndex, 'label', e.target.value)}
                      className="flex-1 min-w-0 text-sm p-2 border border-gray-200"
                      placeholder="PRIVACY POLICY"
                    />
                    <input
                      type="text"
                      value={link.url}
                      onChange={(e) => updateBottomLink(linkIndex, 'url', e.target.value)}
                      className="flex-1 min-w-0 text-sm p-2 border border-gray-200 font-mono"
                      placeholder="/support?section=privacy"
                    />
                    <button
                      onClick={() => removeBottomLink(linkIndex)}
                      className="text-gray-400 hover:text-red-600"
                      title="Remove link"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={addBottomLink}
                  className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-black mt-2"
                >
                  <Plus size={12} /> Add Link
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FooterManager;
